import { useEffect, useRef, useCallback } from 'react'

/**
 * Hook that hides/shows a FAB on scroll direction change
 * WITHOUT triggering React re-renders.
 *
 * Returns a ref callback — attach it to the FAB element.
 * The hook directly manipulates the element's transform style.
 */
export const useScrollFab = (threshold: number = 100) => {
  const lastScrollY = useRef(0)
  const isVisible = useRef(true)
  const fabEl = useRef<HTMLElement | null>(null)

  const applyTransform = () => {
    if (fabEl.current) {
      fabEl.current.style.transform = isVisible.current
        ? 'translateY(0)'
        : 'translateY(120px)'
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const prev = lastScrollY.current
      lastScrollY.current = currentScrollY

      let changed = false
      if (currentScrollY > prev && currentScrollY > threshold) {
        if (isVisible.current) { isVisible.current = false; changed = true }
      } else if (currentScrollY < prev) {
        if (!isVisible.current) { isVisible.current = true; changed = true }
      }

      if (changed) applyTransform()
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [threshold])

  // Ref callback — called when the FAB mounts/unmounts
  const fabRef = useCallback((node: HTMLElement | null) => {
    fabEl.current = node
    if (node) applyTransform()
  }, [])

  return fabRef
}
